import React from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus } from 'react-icons/fa';
import SelectTextField from '../../../../molecules/SelectTextField';
import CurrencyInput from '../../../../molecules/CurrencyInput';
import TextEdit from '../../../../molecules/TextEdit';
import { RealEstateItem } from '../../types/operations';

interface RealEstateItemFormProps {
    items: RealEstateItem[];
    onAdd: (item: RealEstateItem) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
}

const MIN_ROWS = 5;

const generateId = (): string =>
    crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const legalRegimeOptions = [
    { value: 'AF', label: 'Alienação Fiduciária' },
    { value: 'PENHOR', label: 'Penhor' },
];

const propertyTypeOptions = [
    { value: 'RURAL', label: 'Rural' },
    { value: 'URBAN', label: 'Urbano' },
    { value: 'MIXED', label: 'Misto' },
];

const formatCurrency = (val: number): string =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const RealEstateItemForm: React.FC<RealEstateItemFormProps> = ({ items, onAdd, onRemove, disabled = false }) => {
    const legalRegime = useSignal('');
    const propertyType = useSignal('');
    const name = useSignal('');
    const value = useSignal(0);
    const registrationNumber = useSignal('');
    const registryOffice = useSignal('');
    const areaHectares = useSignal('');
    const addressCity = useSignal('');
    const addressState = useSignal('');
    const carNumber = useSignal('');
    const description = useSignal('');

    const resetForm = () => {
        legalRegime.value = '';
        propertyType.value = '';
        name.value = '';
        value.value = 0;
        registrationNumber.value = '';
        registryOffice.value = '';
        areaHectares.value = '';
        addressCity.value = '';
        addressState.value = '';
        carNumber.value = '';
        description.value = '';
    };

    const handleAdd = () => {
        if (!legalRegime.value) { toast.error('Selecione o regime legal'); return; }
        if (!propertyType.value) { toast.error('Selecione o tipo de imóvel'); return; }
        if (!name.value.trim()) { toast.error('Informe o nome da propriedade'); return; }
        if (value.value <= 0) { toast.error('Informe o valor'); return; }

        onAdd({
            collateral_id: generateId(),
            legal_regime: legalRegime.value as RealEstateItem['legal_regime'],
            property_type: propertyType.value as RealEstateItem['property_type'],
            name: name.value.trim(),
            value: value.value,
            registration_number: registrationNumber.value.trim() || undefined,
            registry_office: registryOffice.value.trim() || undefined,
            area_hectares: areaHectares.value ? Number(areaHectares.value) : undefined,
            address_city: addressCity.value.trim() || undefined,
            address_state: addressState.value.trim() || undefined,
            car_number: carNumber.value.trim() || undefined,
            description: description.value.trim() || undefined,
        });
        resetForm();
    };

    const emptyRowCount = Math.max(0, MIN_ROWS - items.length);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-3">
                    <SelectTextField label="Regime Legal *" items={legalRegimeOptions} value={legalRegime.value} onChange={(e) => { legalRegime.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <SelectTextField label="Tipo de Imóvel *" items={propertyTypeOptions} value={propertyType.value} onChange={(e) => { propertyType.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <TextEdit label="Nome da Propriedade *" value={name.value} onChange={(e) => { name.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <CurrencyInput label="Valor (R$) *" value={value.value} onChange={(v) => { value.value = v ?? 0; }} disabled={disabled} fractionDigits={2} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Matrícula" value={registrationNumber.value} onChange={(e) => { registrationNumber.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Cartório" value={registryOffice.value} onChange={(e) => { registryOffice.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Área (ha)" type="number" value={areaHectares.value} onChange={(e) => { areaHectares.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Cidade" value={addressCity.value} onChange={(e) => { addressCity.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-1">
                    <TextEdit label="UF" value={addressState.value} onChange={(e) => { addressState.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="CAR" value={carNumber.value} onChange={(e) => { carNumber.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-1 flex items-end">
                    <button type="button" onClick={handleAdd} disabled={disabled} className="flex items-center gap-1 px-3 py-2 mb-4 bg-gray-600 dark:bg-gray-500 text-white text-sm rounded hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
                        <FaPlus size={12} />
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                    <thead className="text-xs uppercase bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                        <tr>
                            <th className="px-3 py-2">Regime</th>
                            <th className="px-3 py-2">Tipo</th>
                            <th className="px-3 py-2">Nome</th>
                            <th className="px-3 py-2">Valor</th>
                            <th className="px-3 py-2">Matrícula</th>
                            <th className="px-3 py-2">Área (ha)</th>
                            <th className="px-3 py-2">Cidade/UF</th>
                            <th className="px-3 py-2 w-16">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.collateral_id} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">{item.legal_regime === 'AF' ? 'Alienação Fid.' : 'Penhor'}</td>
                                <td className="px-3 py-2">{propertyTypeOptions.find(o => o.value === item.property_type)?.label || item.property_type}</td>
                                <td className="px-3 py-2">{item.name}</td>
                                <td className="px-3 py-2">{formatCurrency(item.value)}</td>
                                <td className="px-3 py-2">{item.registration_number || '-'}</td>
                                <td className="px-3 py-2">{item.area_hectares || '-'}</td>
                                <td className="px-3 py-2">{[item.address_city, item.address_state].filter(Boolean).join('/') || '-'}</td>
                                <td className="px-3 py-2">
                                    <button type="button" onClick={() => onRemove(index)} disabled={disabled} className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50" title="Remover">
                                        <FaTimes size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`empty-${i}`} className={`${(items.length + i) % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RealEstateItemForm;
